import { supabase } from "@/integrations/supabase/client";

/**
 * Upload an image directly to Supabase Storage and return the public URL.
 * Uses existing RLS policies for security - users can only upload to their own folder.
 */
export const uploadImageToStorage = async (
  imageData: string,
  type: string = 'original'
): Promise<string | null> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user for upload');
      return null;
    }

    // Parse base64 data (handle data URL format)
    let base64Data = imageData;
    let mimeType = 'image/jpeg';

    if (imageData.startsWith('data:')) {
      const matches = imageData.match(/^data:([^;]+);base64,(.+)$/);
      if (matches) {
        mimeType = matches[1];
        base64Data = matches[2];
      }
    }

    // Convert base64 to Uint8Array
    const binaryData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));

    // Determine file extension
    const ext = mimeType.includes('png') ? 'png' : mimeType.includes('webp') ? 'webp' : 'jpg';

    // Generate unique filename: {user_id}/{timestamp}_{type}.{ext}
    const timestamp = Date.now();
    const filePath = `${user.id}/${timestamp}_${type}.${ext}`;

    // Upload directly to storage
    const { error } = await supabase.storage
      .from('design-images')
      .upload(filePath, binaryData, {
        contentType: mimeType,
        upsert: false,
        // Filenames embed a unique timestamp, so each URL is content-versioned
        // and safe to cache aggressively. 1 year = 31536000 seconds.
        cacheControl: '31536000',
      });

    if (error) {
      console.error('Upload error:', error);
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('design-images')
      .getPublicUrl(filePath);

    console.log(`Image uploaded successfully: ${filePath}`);
    return publicUrl;
  } catch (error) {
    console.error('Failed to upload image:', error);
    return null;
  }
};
